import type { ReactNode } from "react";
import type { SiteInfo } from "@/components/UI/SitesMap/types";
import { PortfolioMetricTile } from "@/views/Portfolio/shared";
import {
  formatDecimal,
  formatNumberWithGrouping,
} from "@/utils/numberFormatting";
import useIsFeatureFlagEnabled from "@/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import InfoTooltip from "@/components/UI/InfoTooltip";
import {
  RiskBandChip,
  RiskFactorBreakdown,
  useSiteRisk,
} from "@/components/Risk/siteRisk";
import SiteTabTemplate from "../components/SiteTabTemplate";
import SiteMetricTable from "../components/SiteMetricTable";

type Props = { siteInfo: SiteInfo | null };

/**
 * Risk for one site on one trial, banded by trial lifecycle: startup and
 * activation, then conduct quality (RBQM), then close-out. Each band is owned by
 * its own feature flag — RISKS has always owned the RBQM band, so a tenant
 * without the lifecycle flag sees exactly the view it saw before.
 *
 * The bands answer different questions for different readers: will this site
 * activate on time (start-up), is the data trustworthy while it runs (RBQM),
 * and how painful will it be to close (close-out).
 */
function BandHeading({ title, note }: { title: string; note?: string }) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[#556C75]">
        {title}
      </span>
      {note ? <InfoTooltip title={note} /> : null}
    </div>
  );
}

/** A tile whose value this data feed cannot produce yet, with the reason why. */
function UnavailableTile({ label, reason }: { label: string; reason: string }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8C8C8C]">
        {label}
      </div>
      <div className="mt-1 flex items-center gap-1">
        <span className="text-[20px] leading-6 text-[#B0BEC5]">—</span>
        <InfoTooltip title={reason} />
      </div>
    </div>
  );
}

function MetricsShell({
  columns,
  children,
}: {
  columns: number;
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div
        className="grid divide-x divide-[#F0F2F4] border-b border-[#F0F2F4]"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {children}
      </div>
    </div>
  );
}

export default function RiskTab({ siteInfo }: Props) {
  const s = siteInfo;
  const isFlagEnabled = useIsFeatureFlagEnabled();
  const lifecycleBands = isFlagEnabled(FEATURE_FLAGS.SITE_RISK_LIFECYCLE);
  // Without the lifecycle flag this tab has always been the RBQM view, and
  // tenants that skip tab gating reach it regardless of their flags — so keep
  // showing the RBQM band there. Only a tenant that opted into lifecycle bands
  // has to hold RISKS to get it.
  const qualityBand = isFlagEnabled(FEATURE_FLAGS.RISKS) || !lifecycleBands;

  const { data: risk } = useSiteRisk(
    s?.golden_site_id ?? s?.site,
    lifecycleBands,
  );
  const outstandingMilestones = risk?.outstanding_milestone_count ?? null;

  const enrolled = s?.enrolled_count ?? 0;
  const target = s?.subject_count ?? 0;
  const enrollmentPct = target > 0 ? Math.round((enrolled / target) * 100) : 0;

  const screenFailureRate = s?.screen_failure_rate_derived ?? 0;
  const enrollmentRate = s?.enrollment_rate_derived ?? 0;

  const totalPDs =
    (s?.participant_protocol_deviations_count ?? 0) +
    (s?.site_protocol_deviations_count ?? 0);
  const openQueries = s?.open_queries_count_derived ?? 0;
  const discrepancies = s?.data_discrepancies_count ?? 0;
  const longOpenMvrItems = s?.long_open_mvr_action_items_count_derived ?? 0;
  const missingDataPct = s?.missing_data_percent ?? 0;

  const tableRows = [
    {
      label: "Enrollment % to Plan",
      value: `${formatNumberWithGrouping(enrollmentPct)}%`,
    },
    {
      label: "Enrollment Rate",
      value: `${formatDecimal(enrollmentRate, 2)} ${s?.enrollment_rate_unit_derived ?? "sbjs/mo"}`,
    },
    {
      label: "Screen Failure Rate",
      value: `${formatDecimal(screenFailureRate, 1)}%`,
    },
    {
      label: "Protocol Deviations",
      value: formatNumberWithGrouping(totalPDs),
    },
    { label: "Open Queries", value: formatNumberWithGrouping(openQueries) },
    {
      label: "Data Discrepancies",
      value: formatNumberWithGrouping(discrepancies),
    },
    {
      label: "Long-Open MVR Items",
      value: formatNumberWithGrouping(longOpenMvrItems),
    },
    { label: "Missing Data", value: `${formatDecimal(missingDataPct, 1)}%` },
    ...(lifecycleBands
      ? [
          {
            label: "Delay Risk Score",
            value: risk?.scored ? (risk.score?.toFixed(2) ?? "—") : "—",
          },
          {
            label: "Risk Classification",
            value: risk?.classification ?? "—",
          },
          {
            label: "Startup Milestones Outstanding",
            value:
              outstandingMilestones != null
                ? formatNumberWithGrouping(outstandingMilestones)
                : "—",
          },
        ]
      : []),
  ];

  const startupBand = lifecycleBands ? (
    <>
      <BandHeading
        title="Startup & activation risk"
        note="Likelihood this site is slow to activate, scored across every study it has run. Milestones are classified from ISF document activity, so they are inferred rather than read from a milestone feed."
      />
      {risk?.scored ? (
        <>
          <MetricsShell columns={4}>
            <div className="px-4 py-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8C8C8C]">
                DELAY RISK SCORE
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-[20px] font-semibold leading-6 text-[#1F2933] tabular-nums">
                  {risk.score?.toFixed(2)}
                </span>
                {risk.classification ? (
                  <RiskBandChip classification={risk.classification} />
                ) : null}
              </div>
            </div>
            <PortfolioMetricTile
              label="CONFIDENCE"
              value={risk.confidence != null ? risk.confidence.toFixed(2) : "—"}
            />
            <PortfolioMetricTile
              label="STUDIES PARTICIPATED"
              value={formatNumberWithGrouping(risk.studies_participated ?? 0)}
            />
            <PortfolioMetricTile
              label="MILESTONES OUTSTANDING"
              value={formatNumberWithGrouping(outstandingMilestones ?? 0)}
            />
          </MetricsShell>
          <RiskFactorBreakdown factors={risk.factors ?? []} />
        </>
      ) : (
        <MetricsShell columns={1}>
          <UnavailableTile
            label="DELAY RISK SCORE"
            reason="Too little evidence to score this site: no document corpus or startup milestones resolve to it. A data gap, not a low-risk finding."
          />
        </MetricsShell>
      )}
    </>
  ) : null;

  const closeoutSignals =
    (outstandingMilestones ?? 0) + openQueries + longOpenMvrItems;

  const closeoutBand = lifecycleBands ? (
    <>
      <BandHeading
        title="Close-out risk"
        note="How much work stands between this site and a clean close: document debt, open items, and lapsed credentials. Activation-package gaps still open on an active site are retrospective compliance findings."
      />
      {closeoutSignals === 0 ? (
        <MetricsShell columns={1}>
          <div className="px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8C8C8C]">
              CLOSE-OUT
            </div>
            <div className="mt-1 flex items-center gap-1">
              <span className="text-[13px] text-[#48636F]">Upcoming</span>
              <InfoTooltip title="Nothing outstanding that would complicate close-out yet. This band fills in as document debt and open items accumulate." />
            </div>
          </div>
        </MetricsShell>
      ) : (
        <MetricsShell columns={4}>
          <PortfolioMetricTile
            label="ACTIVATION GAPS OUTSTANDING"
            value={formatNumberWithGrouping(outstandingMilestones ?? 0)}
          />
          <PortfolioMetricTile
            label="OPEN MONITOR QUERIES"
            value={formatNumberWithGrouping(openQueries)}
          />
          <UnavailableTile
            label="LAPSED CREDENTIALS"
            reason="Needs document expiration dates at scale; the current extract carries only a handful."
          />
          <UnavailableTile
            label="LSLV TO CLOSURE"
            reason="Needs last-subject-last-visit and site closure dates, which this feed does not carry."
          />
        </MetricsShell>
      )}
    </>
  ) : null;

  const dashboard = (
    <div className="flex flex-col gap-4">
      {startupBand}
      {qualityBand ? (
        <BandHeading
          title="Quality risk (RBQM)"
          note="Conduct-phase indicators for risk and quality staff: enrollment, data quality and deviations while the site is running."
        />
      ) : null}
      {qualityBand ? (
        <>
          <MetricsShell columns={4}>
            <PortfolioMetricTile
              label="ENROLLMENT % TO PLAN"
              value={formatNumberWithGrouping(enrollmentPct)}
              suffix="%"
              progress={enrollmentPct}
            />
            <PortfolioMetricTile
              label="SCREEN FAILURE RATE"
              value={formatDecimal(screenFailureRate, 1)}
              suffix="%"
              progress={screenFailureRate}
              progressTone="gold"
            />
            <PortfolioMetricTile
              label="PROTOCOL DEVIATIONS"
              value={formatNumberWithGrouping(totalPDs)}
            />
            <PortfolioMetricTile
              label="OPEN QUERIES"
              value={formatNumberWithGrouping(openQueries)}
            />
          </MetricsShell>
          <MetricsShell columns={3}>
            <PortfolioMetricTile
              label="DATA DISCREPANCIES"
              value={formatNumberWithGrouping(discrepancies)}
            />
            <PortfolioMetricTile
              label="LONG-OPEN MVR ITEMS"
              value={formatNumberWithGrouping(longOpenMvrItems)}
            />
            <PortfolioMetricTile
              label="MISSING DATA"
              value={formatDecimal(missingDataPct, 1)}
              suffix="%"
              progress={missingDataPct}
              progressTone="gold"
            />
          </MetricsShell>
        </>
      ) : null}
      {closeoutBand}
    </div>
  );

  return (
    <SiteTabTemplate
      tableContent={<SiteMetricTable rows={tableRows} />}
      dashboardContent={dashboard}
    />
  );
}
